import React, {useCallback, useContext, useEffect, useState} from "react";
import {useContextSelector} from 'use-context-selector';
import {QuestionForm} from "../QuestionForm/QuestionForm";
import {useParams} from "react-router-dom";
import {PageTitle} from "../../PageTitle/PageTitle";
import {I18nReactContext} from "../../../contexts/I18nContext";
import './QuestionPoolPage.scss'
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import {MainActionButton} from "../../Form/DominantButton/MainActionButton";
import saveIcon from '../../../assets/images/save-full-icon.png'
import {get, map} from 'lodash'
import {QuestionnaireReactContext, QuestionnaireReactContextProvider} from "../../../contexts/QuestionnaireContext/QuestionnaireContext";
import {generateQuestionsWithAIAPI} from "../../../Services/api";
import {AiLoadingElement} from "../../../Utils/AI/AiLoadingElement/AiLoadingElement";
import {CreateWithAIButton} from "../../LearningManagement/WeezBeeCourse/OpenAIWizardButton/CreateWithAIButton";
import {CompanyReactContext} from "../../../contexts/CompanyContext";
import {FeatureFlags} from "../../../Utils/Constants";
import {Modal} from "../../Smart/Modal/Modal";
import {buttonColors} from "../../Pickers/InputTheme";
import {Button} from "../../Form/Button/Button";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import useSignDocTypes from "../../../Hooks/useSignDocTypes";
import {useNavigateAfterSignDocSave} from "../../../Hooks/useNavigateAfterSignDocSave";
import {AIContextGenerator} from "../../LearningManagement/WeezBeeCourse/AIContextGenerator/AIContextGenerator";


const QuestionPoolPage = () => {
    const questionnaireQuestions = useContextSelector(QuestionnaireReactContext, context => context.questionnaireQuestions);
    const questionnaire = useContextSelector(QuestionnaireReactContext, context => context.questionnaire);
    const initQuestionPool = useContextSelector(QuestionnaireReactContext, context => context.initQuestionPool);
    const saveQuestionPool = useContextSelector(QuestionnaireReactContext, context => context.saveQuestionPool);
    const setActiveQuestion = useContextSelector(QuestionnaireReactContext, context => context.setActiveQuestion);

    const {signDocId} = useParams()
    const {i18n} = useContext(I18nReactContext)
    const [isLoading, setIsLoading] = useState(false)
    const [isAILoading, setIsAILoading] = useState(false)
    const [showAICreationModal, setShowAICreationModal] = useState(false)
    const [aiContextData, setAiContextData] = useState(null)
    const companyContext = useContext(CompanyReactContext)
    const [validationErrors, setValidationErrors] = useState(null);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const {getIsAIImplemented} = useSignDocTypes();
    const navigateAfterSignDocSave = useNavigateAfterSignDocSave();
    const signDocType = get(questionnaire, 'type');
    const signDocCategory = get(questionnaire, 'category');

    const init = async () => {
        setIsLoading(true)
        await initQuestionPool({signDocId})
        setIsLoading(false)
    }

    useEffect(() => {
        init()
    }, [])

    /**
     * Stores context data from AIContextGenerator (called on every keystroke)
     * @param {Object} contextData - The context data from AIContextGenerator (includes questionQuantity)
     */
    const handleContextDataChange = useCallback((contextData) => {
        setAiContextData(contextData)
    }, [])

    /**
     * Validates the form data from AIContextGenerator
     * @returns {boolean} True if form is valid
     */
    const validateAIForm = useCallback(() => {
        return aiContextData && aiContextData.subject && aiContextData.subject.trim().length > 0
    }, [aiContextData])

    /**
     * Handles save button click - triggers AI generation
     * Only called when user clicks the Create/Save button in the modal
     */
    const handleAISave = async () => {
        setShowAICreationModal(false)
        setIsAILoading(true)

        try {
            await generateQuestionsWithAIAPI({
                signDocId,
                subject: aiContextData.subject,
                userInstructions: aiContextData.promptInstructions || '',
                fileContent: aiContextData.extractedFileText || '',
                questionQuantity: aiContextData.questionQuantity
            })
            setAiContextData(null) // Reset after use
            await init()
        } catch (error) {
            console.error('Error generating questions:', error)
        } finally {
            setIsAILoading(false)
        }
    }

    /**
     * Handles modal close without saving
     */
    const handleModalClose = useCallback(() => {
        setShowAICreationModal(false)
        setAiContextData(null) // Reset data
    }, [])

    const handleSaveQuestionPool = async () => {
        let validation = await saveQuestionPool()
        if (!validation.isValid) {
            setValidationErrors(validation.errors);
            setShowValidationModal(true);
            return;
        }
        // The timeout use for user experience
        setTimeout(() => {
            // Navigate to category tab after saving
            navigateAfterSignDocSave({
                signDocType,
                signDocCategory,
                signDocId,
                navigateToTab: true
            });
        }, 500);
    }

    const closeValidationModal = useCallback(() => {
        setShowValidationModal(false);
    }, [])

    const navigateToQuestion = useCallback(questionId => {
        setActiveQuestion(questionId);
        closeValidationModal();

        // Scroll to the question element
        setTimeout(() => {
            const questionElement = document.getElementById(`question-${questionId}`);
            if (questionElement) {
                questionElement.scrollIntoView({ behavior: 'smooth' });
                questionElement.classList.add('highlight-question');
                setTimeout(() => {
                    questionElement.classList.remove('highlight-question');
                }, 1300);
            }
        }, 100);
    }, [])

    const handleFocus = (questionId) => {
        setActiveQuestion(questionId);
    };

    const hasAIFF = signDocType && companyContext?.featureFlags?.[FeatureFlags.AI] && getIsAIImplemented({ type: signDocType });

    return (isLoading && !isAILoading ? <LoadingSpinner/> :
        <div className="MenuPage QuestionPoolPage">
            <PageTitle
                title={signDocType ? `${i18n.translate(`questionnaireQuestions.headers.${signDocType}`)}: ${get(questionnaire, 'docName')}` : ''}
                subTitle={`${i18n.translate('questionnaireQuestions.headers.poolQuestionSubTitle')}: ${questionnaireQuestions.length}`}
                size={'small'}
                noLogo
                customComponent={
                    <MainActionButton
                        title={i18n.translate('questionnaireQuestions.buttons.saveQuestions')}
                        subTitle={`${i18n.translate('questionnaireQuestions.buttons.saveQuestionsSubTitle')}`}
                        image={saveIcon}
                        onClick={handleSaveQuestionPool}                    />}/>
            <div className="menuInnerPage">
                {<CreateWithAIButton
                    onClick={() => setShowAICreationModal(true)}
                    isAILoading={isAILoading}
                    disabled={!hasAIFF}
                    signDocType={signDocType}
                />}
                <AIContextGenerator
                    open={showAICreationModal}
                    onClose={handleModalClose}
                    onDataChange={handleContextDataChange}
                    onSave={handleAISave}
                    validateForm={validateAIForm}
                    signDocType={signDocType}
                />
                <div className="questionsContainer">
                    {isAILoading ? <div className="AILoadingContainerWrapper">
                        <AiLoadingElement loadingTexts={[
                            i18n.translate('questionnaireQuestions.aiLoadingTexts.loading'),
                            i18n.translate('questionnaireQuestions.aiLoadingTexts.testWillBeReadySoon'),
                            i18n.translate('questionnaireQuestions.aiLoadingTexts.aBitMorePatience'),
                            i18n.translate('questionnaireQuestions.aiLoadingTexts.pleaseWait'),
                            i18n.translate('questionnaireQuestions.aiLoadingTexts.itMightTakeABitLongerButItsWorthIt'),
                        ]}/>
                    </div> : ''}
                    {questionnaireQuestions || isAILoading ? map(questionnaireQuestions, (question) => (
                        <div key={`${question.id}-div`} id={`question-${question.id}`}>
                            <QuestionForm
                                key={question.id}
                                questionId={question.id}
                                onFocus={handleFocus}
                            />
                        </div>
                    )) : <LoadingSpinner/>}
                </div>
                <Modal
                    open={showValidationModal}
                    onClose={closeValidationModal}>
                    <div className="validation-error-modal modalWrapper">
                        <div className="modalTitle">
                            <div>{i18n.translate('questionnaireQuestions.validation.modalTitle')}</div>
                        </div>
                        <div className="modalContent">
                            <div className="validation-error-list">
                                {validationErrors?.map((questionError, index) => (
                                    <div key={`error-${index}`} className="question-error-item">
                                        <div className="question-error-header">
                                            <span className="question-number">{`${i18n.translate('questionnaireQuestions.validation.questionIndexError')}: ${get(questionError, 'questionPosition') +1}`}</span>
                                            <ul className="error-list">
                                                {questionError.errors.map((error, errorIndex) => (
                                                    <div key={`error-detail-${errorIndex}`} className="error-item">
                                                        <WarningAmberIcon fontSize="medium"/>
                                                        <span className="error-message">{error.errorMessage}</span>
                                                    </div>
                                                ))}
                                            </ul>
                                            <Button
                                                color={buttonColors.GOLD}
                                                onClick={() => navigateToQuestion(get(questionError, 'questionId'))}
                                                text={i18n.translate('questionnaireQuestions.validation.navigateToQuestion')}
                                            />
                                        </div>


                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="modalFooter">
                            <Button
                                color={buttonColors.GREY}
                                onClick={closeValidationModal}
                                text={i18n.translate('questionnaireQuestions.validation.closeAndFix')}
                            />
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
        )
}

export const QuestionPoolPageWrapped = () => {
    return <QuestionnaireReactContextProvider>
        <QuestionPoolPage/>
    </QuestionnaireReactContextProvider>
}